import { MailIcon, XIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import { delay, tone } from '../../primitives';

/**
 * The lead form in a college's chat, filled in by a prospective student,
 * and the email the admissions team gets for it.
 */
export default function EnquiryForm({
  brand,
  name,
}: {
  brand: string;
  name: string;
}) {
  const { t } = useLocale('bot-use-cases');
  const { t: tc } = useLocale('chatbots');

  const fields = [
    { label: t('Your name'), value: 'Priya Sharma' },
    { label: t('Your email'), value: 'priya@example.com' },
    { label: t('Your phone'), value: '+44 7700 900123' },
  ];

  return (
    <div className='relative pb-0 sm:pb-20 lg:pe-16'>
      <div className='mx-auto max-w-[24rem] overflow-hidden rounded-[22px] bg-white shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)] ring-1 ring-black/5 dark:bg-[#16151f] dark:ring-white/10'>
        <div className='flex items-center gap-3 border-b border-black/[0.06] px-5 py-4 dark:border-white/[0.06]'>
          <span
            className={clsxm(
              'flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-semibold text-white',
              brand,
            )}
          >
            {name.charAt(0)}
          </span>
          <p className={clsxm('truncate text-[15px] font-semibold', tone.ink)}>
            {name}
          </p>
        </div>

        <div className='space-y-3 px-5 py-5 text-[14px] leading-relaxed'>
          <p
            className={clsxm(
              'max-w-[88%] rounded-2xl rounded-es-md px-4 py-2.5 text-white',
              brand,
            )}
          >
            {t(
              'The Computer Science course starts in September, with places for 120 students.',
            )}
          </p>

          <div className='rounded-2xl bg-[#f6f6f9] p-4 ring-1 ring-black/[0.05] dark:bg-white/[0.05] dark:ring-white/[0.06]'>
            <p className='flex items-start justify-between gap-3'>
              <span className={clsxm('text-[14px] font-semibold', tone.ink)}>
                {t('Get our prospectus')}
              </span>
              <XIcon className={clsxm('h-4 w-4 flex-none', tone.quiet)} />
            </p>
            <div className='mt-3 space-y-2'>
              {fields.map(({ label, value }, index) => (
                <div
                  key={label}
                  className={clsxm(
                    styles.rv,
                    'rounded-xl bg-white px-3 py-2 ring-1 ring-black/[0.08] dark:bg-white/[0.06] dark:ring-white/10',
                  )}
                  style={delay(200 + index * 150)}
                >
                  <span className={clsxm('block text-[11px]', tone.quiet)}>
                    {label}
                  </span>
                  <span
                    className={clsxm('block text-[13px] font-medium', tone.ink)}
                    dir='ltr'
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
            <span
              className={clsxm(
                'mt-3 flex w-full items-center justify-center rounded-xl py-2.5 text-[13px] font-semibold text-white',
                brand,
              )}
            >
              {t('Send')}
            </span>
          </div>
        </div>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'mt-4 flex items-start gap-3 rounded-2xl p-4 shadow-[0_30px_60px_-24px_rgba(0,0,0,0.65)] sm:absolute sm:bottom-0 sm:end-0 sm:mt-0 sm:w-[18.5rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(800)}
      >
        <span className='flex h-9 w-9 flex-none items-center justify-center rounded-full bg-primary-600 text-white'>
          <MailIcon className='h-4 w-4' />
        </span>
        <span className='min-w-0 flex-1'>
          <span className='flex items-baseline justify-between gap-2'>
            <span className={clsxm('text-[13px] font-semibold', tone.ink)}>
              {t('New enquiry')}
            </span>
            <span className={clsxm('text-[11px]', tone.quiet)}>
              {tc('Just now')}
            </span>
          </span>
          <span className={clsxm('mt-0.5 block text-xs leading-5', tone.body)}>
            Priya Sharma · priya@example.com
          </span>
          <span className={clsxm('block text-xs leading-5', tone.quiet)}>
            {t('Sent to your admissions team')}
          </span>
        </span>
      </div>
    </div>
  );
}
